import { useState, useMemo, useEffect, useCallback } from "react";
import { SortingState } from "@tanstack/react-table";
import { useNavigate, useParams } from "react-router-dom";

import { Breadcrumbs } from "ui/breadcrumbs";
import { Button } from "ui/button";
import { Accordion } from "ui/accordion";
import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import { DataGrid, TableLoader, TableItemsNotFound } from "ui/data-grid";

import {
  fetchBatchDetails,
  fetchBatchTouchpointVersions,
  useApproveBatch,
} from "hooks/api/use-batches";

import {
  useGetTouchpointVersionStatusesQuery,
  useGetBatchStatusesQuery,
} from "hooks/api/dictionary";
import { useCurrentUser } from "hooks/api/use-current-user";

import { createBatchTouchpointsTableColumns } from "containers/client/reviews/approval-queue/batch/batch-touchpoints-table-columns";
import { hasPermission } from "utilities/user";
import { getBatchStatuses } from "containers/admin/clients/client/approval-queue/approval-queue.helpers";
import { formatDueDate, formatInputDate } from "utilities/date";

import { Route as AppRoute } from "utilities/app-routes";

import { Permission } from "types/auth";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

import {
  BatchItem,
  TouchpointItem,
} from "containers/admin/clients/client/approval-queue/approval-queue.types";

import Icons from "assets";

const REVIEWS_APPROVAL_QUEUE_PATH = `${AppRoute.reviews}/${AppRoute.approvalQueue}`;

const BatchDetails = () => {
  const navigate = useNavigate();
  const { id: batchId } = useParams();
  const currentUser = useCurrentUser();
  const clientId = currentUser.client?.id;
  const shouldShowBreadcrumbs = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const [isLoading, setIsLoading] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<BatchItem | null>(null);
  const [currentBatchTPVersions, setCurrentBatchTPVersions] = useState<TouchpointItem[]>([]);

  // Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pagesAmount, setPagesAmount] = useState(0);
  const [{ pageSize = 25, totalRecords = 0 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  // Sorting state
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "touchpointName", desc: false },
  ]);

  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();
  const { data: batchStatuses = [] } = useGetBatchStatusesQuery();

  const approveBatchMutation = useApproveBatch({ clientId });

  const isClientAdmin =
    hasPermission(currentUser, Permission.PERM_CLIENT_USER) &&
    hasPermission(currentUser, Permission.PERM_CLIENT_APPROVAL);

  const handleApproveBatch = async () => {
    if (!clientId || !currentBatch?.id) return;

    await approveBatchMutation.mutateAsync({ batchId: currentBatch.id });
    navigate(`${AppRoute.reviews}/${AppRoute.approvalQueue}`);
  };

  // Batch actions
  const fetchBatch = useCallback(async () => {
    try {
      if (!clientId) {
        return;
      }

      const batch = await fetchBatchDetails({ clientId: clientId, batchId: batchId! });

      batch.dueDate = formatInputDate(batch.dueDate);

      setCurrentBatch(batch);
    } catch (error) {
      setCurrentBatch(null);
    }
  }, [clientId, batchId]);

  const fetchTPVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!clientId) {
        return;
      }

      const batchTPVersions = await fetchBatchTouchpointVersions({
        clientId: clientId,
        batchId: batchId!,
        options: {
          size: pageSize,
          page: pageIndex,
          sort: sortingParams
            .map((param) => `${param.id},${param.desc ? "desc" : "asc"}`)
            .join(","),
        },
      });

      batchTPVersions && setCurrentBatchTPVersions(batchTPVersions.items);

      setPagesAmount(batchTPVersions.totalPages);
      setAmount({ pageSize, totalRecords: batchTPVersions.totalItems });
    } catch (error) {
      setCurrentBatchTPVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, batchId, sortingParams, pageSize, pageIndex]);

  // Create table columns
  const columns = useMemo(
    () =>
      createBatchTouchpointsTableColumns({
        clientId: clientId!,
        touchpointVersionStatuses,
        hasTemplates: currentBatchTPVersions.some(
          (tp) => tp.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME,
        ),
        isClientAdmin,
      }),
    [clientId, touchpointVersionStatuses, currentBatchTPVersions, isClientAdmin],
  );

  useEffect(() => {
    clientId && batchId && fetchBatch();
  }, [clientId, batchId, fetchBatch]);

  useEffect(() => {
    currentBatch && fetchTPVersions();
  }, [currentBatch, fetchTPVersions]);

  return (
    currentBatch?.name && (
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden bg-gray-100">
        <div className="shrink-0 pt-3 pl-3 pr-5 pb-4">
          {shouldShowBreadcrumbs && (
            <div className="mb-4 flex h-5 items-center">
              <Breadcrumbs
                breadcrumbs={[
                  { label: "Reviews", href: AppRoute.reviews },
                  { label: "Approval queue", href: REVIEWS_APPROVAL_QUEUE_PATH },
                  { label: currentBatch.name },
                ]}
              />
            </div>
          )}
          <div className="flex w-full shrink-0 items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold leading-[30px] text-navy-800">
                {currentBatch?.name}
              </h1>
            </div>
            {isClientAdmin && (
              <Button onClick={handleApproveBatch} aria-label="Approve batch">
                Approve batch
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 pb-3">
            <div className="flex gap-2 rounded-md bg-warning-500/20 px-3 py-1.5 text-sm font-semibold shadow-xs">
              <Icons.Event className="size-5 text-warning-700" />
              <span className="text-warning-700">{formatDueDate(currentBatch?.dueDate)}</span>
            </div>
            {currentBatch?.status && (
              <StatusIndicator
                versionStatuses={getBatchStatuses(batchStatuses, currentBatch.status) ?? []}
              />
            )}
          </div>

          <Accordion variant="secondary" title="Batch description">
            <p className="whitespace-pre-wrap">{currentBatch?.description}</p>
          </Accordion>
        </div>

        <div className="min-h-0 flex-1 w-full overflow-y-auto custom-scroll">
          <div className="pl-3 pr-5 pb-5">
            <TableLoader isLoading={isLoading} fullScreen />

            {!isLoading &&
              (currentBatchTPVersions?.length ? (
                <DataGrid
                  columns={columns}
                  data={currentBatchTPVersions || []}
                  isLoading={false}
                  showingRecordsIsOn={true}
                  viewByAmountSelectIsOn={true}
                  sortingParams={sortingParams}
                  setSortingParams={setSortingParams}
                  manualSorting={true}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pagesAmount={pagesAmount}
                  setAmount={setAmount}
                  pageIndex={pageIndex}
                  setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                />
              ) : (
                <TableItemsNotFound />
              ))}
          </div>
        </div>
      </div>
    )
  );
};

export default BatchDetails;
